import { useIsMutating } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { produce } from 'immer'
import { useState } from 'react'
import { useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { FlowTypes } from 'v2/types'
import { errorToast, successToast, warningToast } from 'v2/utils/toasts'
import type { ZodCustomIssue } from 'zod'
import type { AgentSettingsFormValues } from '../config/base.config'
import { useGetProviders } from '../knowledge-base/queries'
import { usePhloMutation } from '../queries'
import { useFlowStore } from '../store'
import {
  AgentState,
  type FlowData,
  FlowNodeTypes,
  type FlowUpsertResponse,
  type GenericEdge,
  type GenericNode,
  type RFlowPayload,
} from '../types'
import { agentDefaultTimezone } from '../utils/agent-default-timezone'
import { filterEmptyEventCallbacks } from '../utils/agent-settings-utils'
import { removeOrphanIntents } from '../utils/ai-agent-utils'
import { transformFlowData } from '../utils/data-transformer'
import {
  handleBackendValidationErrors,
  MultipleNodeValidationError,
  validateFlowContentForUi,
} from '../utils/flow-validation'
import { isStartNodeWired } from '../utils/start-node-wired'
import { validateVoiceAiConfig } from '../utils/stt-tts-validator'
import {
  mapBackendNodeToFrontend,
  processValidationErrors,
} from '../utils/validation-error-processor'
import { kbIdsForStoreSync, kbLinksForSave } from './kb-links-for-save'
import { subAuthIdPayloadPart } from './sub-auth-id-for-save'
import { useTypedReactFlow } from './use-react-flow'

function getSuccessDescription(isPlayground: boolean | undefined, isDraft: boolean): string {
  if (isPlayground) {
    return 'Agent Flow Playground is ready for testing.'
  }
  if (isDraft) {
    return 'Agent Flow saved as draft successfully.'
  }
  return 'Agent Flow published successfully.'
}

export const usePublishFlow = () => {
  // The mutation is keyed on the route id (usePhloMutation), so the pending watcher
  // must be too — keying on flowData.phlo.id missed first saves of a new flow, whose
  // cached phlo has no id yet, leaving the 'Publishing...' state stuck false.
  const { id: routeId } = useParams()
  const { mutateAsync: mutateFlowAsync } = usePhloMutation()
  const mutationCount = useIsMutating({
    mutationKey: flowQueryKeys().flowData(routeId ?? 'unsaved'),
  })
  const isPending = mutationCount > 0
  const { getNodes, getEdges, updateNodeData, updateEdge } = useTypedReactFlow()
  const setIsEditing = useFlowStore((state) => state.setIsEditing)

  const toggleValidationErrorPopover = useFlowStore((state) => state.toggleValidationErrorPopover)
  const { data: providers } = useGetProviders()
  const { data: profileData } = useProfileQuery()
  const { isLivekitEnabled, isLoading: isLivekitEnabledLoading } = useLivekitEnabled()

  // Track which action is being performed
  const [actionType, setActionType] = useState<'draft' | 'publish' | null>(null)

  const handleSuccess = async (
    response: FlowUpsertResponse,
    isDraft: boolean,
    options:
      | {
          isDraft?: boolean
          name?: string
          onSuccess?: () => void
          selectedKBs?: string[]
          isPlayground?: boolean
          onResult?: (result: {
            status: 'saved' | 'saved_with_validation_errors' | 'failed'
            validation_errors?: unknown
            message?: string
            phlo_uuid?: string
            node_map?: Record<string, string>
          }) => void
          skipNavigate?: boolean
          agentSettingsOverride?: AgentSettingsFormValues
        }
      | undefined,
    nodeMap?: Record<string, string>,
    normalizedAgentSettingsForSave?: AgentSettingsFormValues,
  ) => {
    const hasValidationErrors =
      isDraft &&
      response?.validation_errors &&
      Object.keys(response.validation_errors as object).length > 0

    if (hasValidationErrors) {
      warningToast({
        title: 'Agent Flow saved as draft',
        description:
          'Your Agent Flow has been saved as a draft, but contains validation errors. Please resolve these issues before publishing.',
      })
    } else if (!options?.isPlayground) {
      successToast({
        title: 'Success',
        description: getSuccessDescription(options?.isPlayground, isDraft),
      })
    }

    options?.onResult?.({
      status: hasValidationErrors ? 'saved_with_validation_errors' : 'saved',
      validation_errors: response?.validation_errors,
      phlo_uuid: response?.phlo?.id,
      node_map: nodeMap,
    })

    setIsEditing(false)
    setActionType(null)

    let settingsToSync: AgentSettingsFormValues | null = normalizedAgentSettingsForSave
      ? {
          ...normalizedAgentSettingsForSave,
          schedule: response?.phlo?.schedule ?? normalizedAgentSettingsForSave.schedule,
          conversation_goals:
            response?.phlo?.conversation_goals ?? normalizedAgentSettingsForSave.conversation_goals,
        }
      : null

    if (!settingsToSync && response?.phlo?.conversation_goals) {
      const current = useFlowStore.getState().agentSettings
      if (current) {
        settingsToSync = {
          schedule: current.schedule,
          knowledge_base_ids: current.knowledge_base_ids || [],
          voice_ai_config: current.voice_ai_config,
          event_callbacks: current.event_callbacks,
          conversation_goals: response.phlo.conversation_goals,
        }
      }
    }

    // Sync store from the normalized values that were just saved, so store stays
    // in sync for export/download/vibe agent without triggering a reconstruction
    // via useAgentSettings. Store values match form values so form.reset() is a no-op.
    if (settingsToSync) {
      useFlowStore.getState().setAgentSettings(settingsToSync)
    }

    // Reset editor tracking after successful save
    useFlowStore.getState().resetEditorTracking()

    // Update initial agent settings snapshot so unsaved changes detection
    // compares against the just-saved state, not the original load state
    if (settingsToSync) {
      useFlowStore.getState().setInitialAgentSettings(structuredClone(settingsToSync))
    } else {
      const currentSettings = useFlowStore.getState().agentSettings
      if (currentSettings) {
        useFlowStore.getState().setInitialAgentSettings(structuredClone(currentSettings))
      }
    }

    options?.onSuccess?.()
  }

  const handleError = (
    error: unknown,
    payload: RFlowPayload,
    nodeMap: Record<string, string>,
    options:
      | {
          isDraft?: boolean
          name?: string
          onSuccess?: () => void
          selectedKBs?: string[]
          isPlayground?: boolean
          onResult?: (result: {
            status: 'saved' | 'saved_with_validation_errors' | 'failed'
            validation_errors?: unknown
            message?: string
            phlo_uuid?: string
            node_map?: Record<string, string>
          }) => void
          skipNavigate?: boolean
        }
      | undefined,
  ) => {
    setActionType(null)

    const errorMessage = getErrorText(error as AxiosError)
    options?.onResult?.({
      status: 'failed',
      message: errorMessage || 'Failed to save draft',
    })

    // Embed: a 409 means a preserved credential token went stale, not a node validation
    // error. Refetch the flow so the next open carries fresh tokens.
    if (isEmbedMode() && (error as AxiosError)?.response?.status === 409) {
      if (routeId) {
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowData(routeId) })
      }
      errorToast({
        title: 'A saved credential on this agent changed',
        description: errorMessage || 'Reload the agent to get the latest version, then try again.',
      })
      return
    }

    const currentNodes = getNodes() as GenericNode[]
    const validationErrors = processValidationErrors(error, currentNodes, undefined, nodeMap)

    if (validationErrors.length > 0) {
      const backendValidationIssues = handleBackendValidationErrors(error, payload)
      if (backendValidationIssues.length > 0) {
        const issuesByNodeId = new Map<string, ZodCustomIssue[]>()
        backendValidationIssues.forEach((issue) => {
          const backendNodeId = issue.params?.data?.id
          if (backendNodeId) {
            const matchingFrontendNode = mapBackendNodeToFrontend(
              backendNodeId,
              currentNodes,
              nodeMap,
            )
            if (matchingFrontendNode) {
              const frontendNodeId = matchingFrontendNode.id
              if (!issuesByNodeId.has(frontendNodeId)) {
                issuesByNodeId.set(frontendNodeId, [])
              }
              const updatedIssue = {
                ...issue,
                params: {
                  ...issue.params,
                  data: { ...issue.params?.data, id: frontendNodeId },
                },
              }
              issuesByNodeId.get(frontendNodeId)?.push(updatedIssue)
            }
          }
        })

        issuesByNodeId.forEach((issues, frontendNodeId) => {
          updateNodeData(frontendNodeId, (nodeData: GenericNode) => {
            const result = produce({ meta: nodeData.data.meta || {} }, (draft) => {
              draft.meta.issues = issues
            })
            return result
          })
        })
      }

      toggleValidationErrorPopover()
      errorToast({
        title: 'Agent Flow validation failed',
        description: options?.isPlayground
          ? 'Your Agent Flow contains errors that prevent testing. Please review and fix all highlighted issues.'
          : 'Unable to save your Agent Flow due to validation errors. Please fix the highlighted issues and try again.',
      })
    } else {
      errorToast({
        title: 'Failed to save Agent Flow',
        description:
          errorMessage ||
          (options?.isPlayground
            ? 'Unable to prepare your Agent Flow for testing. Please check your configuration and try again.'
            : 'Failed to save your Agent Flow. Please check your configuration and try again.'),
      })
    }
  }

  const publishFlow = async (
    flowData: FlowData,
    options: {
      isDraft?: boolean
      name?: string
      onSuccess?: () => void
      selectedKBs?: string[]
      isPlayground?: boolean
      onResult?: (result: {
        status: 'saved' | 'saved_with_validation_errors' | 'failed'
        validation_errors?: unknown
        message?: string
        phlo_uuid?: string
        node_map?: Record<string, string>
      }) => void
      skipNavigate?: boolean
      skipQueryInvalidation?: boolean
      agentSettingsOverride?: AgentSettingsFormValues
    } = {},
    idToUse?: string,
  ) => {
    const flowType = useFlowStore.getState().currentFlowType as FlowTypes
    // Use override from form values if provided, avoiding store writes that trigger form resets
    const agentSettings = options.agentSettingsOverride || useFlowStore.getState().agentSettings
    const edges = getEdges() as GenericEdge[]
    const nodes = getNodes() as GenericNode[]
    const isDraft = options.isDraft ?? false
    // ! Hardcoded to skip validation on FE when saving/publishing flow.
    // ! Not removing the validation code entirely as it can be used in future.
    const shouldValidate = false

    // Set the action type based on isDraft
    setActionType(isDraft ? 'draft' : 'publish')

    // Check if there's a start node in the flow
    const hasStartNode = nodes.some((node) => node.type === 'start')
    if (!hasStartNode) {
      errorToast({
        title: 'Missing Trigger Node',
        description:
          'Your Agent Flow must have a Trigger node. Please add a Trigger node to continue.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'Flow must have a Start(Trigger) node',
      })
      return
    }

    // Screening runs only on the LiveKit runtime and the wire component has
    // no degradation path. The palette already hides the tile for non-LiveKit
    // orgs; this guards the paths that put nodes on canvas without the
    // palette (flow import, templates, vibe-agent-written nodes).
    const hasScreeningNode = nodes.some(
      (node) =>
        node.type === FlowNodeTypes.outbound_screening ||
        node.type === FlowNodeTypes.contact_screening,
    )
    if (hasScreeningNode && !isLivekitEnabledLoading && !isLivekitEnabled) {
      errorToast({
        title: 'Node not available',
        description:
          'The Initiate call and screen node is not available on your account yet. Remove it to publish this flow, or contact support to enable it.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'Screening node requires the LiveKit runtime',
      })
      return
    }

    // agent_node is livekit-only at runtime. Like screening, the palette hides
    // it for non-LiveKit orgs, so this unconditional guard covers the paths
    // that put it on canvas without the palette (import, templates, vibe agent).
    // The intent/name/{details} validators live server-side in phlo-core
    // (_check_agent_tasks); this is the client-side org-LiveKit backstop.
    const hasAgentNode = nodes.some((node) => node.type === FlowNodeTypes.agent_node)
    if (hasAgentNode && !isLivekitEnabledLoading && !isLivekitEnabled) {
      errorToast({
        title: 'Node not available',
        description:
          'The Collect input node is not available on your account yet. Remove it to publish this flow, or contact support to enable it.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'agent_node requires the LiveKit runtime',
      })
      return
    }

    // Determine if transitioning from DRAFT state to ACTIVE
    const isTransitioningFromDraft = flowData?.phlo?.state === AgentState.DRAFT && !isDraft

    if (shouldValidate) {
      const validationResult = validateFlowContentForUi(
        nodes,
        edges,
        flowType,
        false,
        isDraft ? 'save' : 'publish',
        isLivekitEnabled,
      )
      if (!validationResult.isValid) {
        if (validationResult.issuesToUpdate) {
          validationResult.issuesToUpdate.forEach((issue) => {
            const issueNodeID = issue.params?.data.id
            if (issueNodeID) {
              updateNodeData(issueNodeID, (o: GenericNode) =>
                produce({ meta: o.data.meta }, (draft) => {
                  draft.meta.issues = [issue]
                }),
              )
            }
          })
        }
        if (validationResult.edgeIssuesToUpdate) {
          validationResult.edgeIssuesToUpdate.forEach(({ edgeId, issue }) => {
            updateEdge(edgeId, (e: GenericEdge) =>
              produce({ data: e.data || { nodeVars: [], meta: {} } }, (draft) => {
                draft.data.meta = draft.data.meta || {}
                draft.data.meta.issues = [issue]
              }),
            )
          })
        }
        if (validationResult.error) {
          errorToast(validationResult.error)
        }
        setActionType(null)
        throw new Error('Validation failed')
      }
    }

    // Validation passed, proceed with existing logic for preparing payload
    let data: Omit<RFlowPayload, 'phlo'>
    let nodeMap: Record<string, string> = {}

    try {
      // Filter AI node intents to remove orphan edges before transforming
      const filteredNodes = nodes.map((node) => {
        // Get all source handles that have connected edges
        const connectedSourceHandles = new Set(
          edges
            .filter((edge) => edge.source === node.id)
            .map((edge) => edge.sourceHandle)
            .filter((handle): handle is string => Boolean(handle)),
        )
        return removeOrphanIntents(node, connectedSourceHandles)
      })

      const { data: transformedData, nodeMap: nodeMapFromTransform } = transformFlowData(
        { nodes: filteredNodes, edges },
        flowType,
        !isDraft,
        isTransitioningFromDraft,
        // Playground saves keep stable node ids so the BE updates nodes in place
        // instead of inserting a duplicate set on every run.
        options.isPlayground || false,
      )
      data = transformedData
      nodeMap = nodeMapFromTransform
    } catch (error) {
      if (error instanceof MultipleNodeValidationError) {
        // Handle collective validation errors - highlight all affected nodes
        const nodeErrors = error.issues || []

        nodeErrors.forEach((nodeError) => {
          if (nodeError.nodeId) {
            updateNodeData(nodeError.nodeId, (o: GenericNode) =>
              produce({ meta: o.data.meta }, (draft) => {
                draft.meta.issues = [
                  {
                    code: 'custom',
                    message: nodeError.message,
                    path: nodeError.path || ['config'],
                    params: { data: { id: nodeError.nodeId } },
                  },
                ]
              }),
            )
          }
        })

        // Show error message - either from first node error or collective message
        const firstNodeError = nodeErrors[0]
        const errorMessage = firstNodeError
          ? firstNodeError.message
          : `Validation failed for ${nodeErrors.length} node(s)`

        errorToast({
          title: 'Agent Flow configuration error',
          description: errorMessage,
        })
      } else {
        errorToast({
          title: 'Agent Flow configuration error',
          description:
            error instanceof Error
              ? error.message
              : 'Unable to process the Agent Flow configuration. Please check your setup and try again.',
        })
      }
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: `Flow transformation failed, error: ${error}`,
      })
      return
    }

    if (!isStartNodeWired(data.nodes, data.connections)) {
      errorToast({
        title: 'Incomplete Agent Flow',
        description:
          'Your Agent Flow needs at least one node connected to the Trigger node. Please add and connect a node to continue.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'Flow must have at least one connection from Trigger',
      })
      return
    }
    // Apply STT/TTS default validation logic before publishing
    const finalVoiceAiConfig = validateVoiceAiConfig(
      agentSettings?.voice_ai_config,
      providers?.data,
    )

    // Validate conversation goals for duplicates (case-insensitive)
    const goals = agentSettings?.conversation_goals ?? []
    const goalNames = goals.map((g) => g.goal_name.toLowerCase().trim()).filter(Boolean)
    if (goalNames.length !== new Set(goalNames).size) {
      errorToast({
        title: 'Duplicate Goal Names',
        description: 'Each conversation goal must have a unique name.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'Duplicate conversation goal names are not allowed',
      })
      return
    }

    // Persist timezone independent of operating-hours window. Schedule object
    // can be timezone-only ({timezone}) — backend Cerberus schema accepts it.
    // Inject the user's org timezone as a default so it persists without
    // requiring the user to interact with the dropdown.
    const normalizedScheduleForSave = (() => {
      const schedule = agentSettings?.schedule || flowData.phlo.schedule
      const orgTimezone = agentDefaultTimezone(profileData?.organizations?.[0]?.timezone)
      const tz = schedule?.timezone || orgTimezone
      const start = schedule?.start_time?.trim()
      const end = schedule?.end_time?.trim()
      if (!tz) return null
      if (start && end) return { timezone: tz, start_time: start, end_time: end }
      return { timezone: tz }
    })()

    const schedule = agentSettings?.schedule || flowData.phlo.schedule
    const start = schedule?.start_time?.trim()
    const end = schedule?.end_time?.trim()
    if ((start && !end) || (!start && end)) {
      errorToast({
        title: 'Incomplete operating hours',
        description: 'Set both start and end time, or leave both blank.',
      })
      setActionType(null)
      options.onResult?.({
        status: 'failed',
        message: 'Operating hours require both start_time and end_time',
      })
      return
    }

    // KB-link normalization + the SER-5976 (F2) anti-wipe rule live in the pure, unit-tested
    // kbLinksForSave (see kb-links-for-save.ts for the full rationale): prefer an explicit
    // selection, then the persisted backend value, then the agentSettings cache; and when the
    // backend flagged the links UNKNOWN (`knowledge_base_ids_unavailable`) with no explicit
    // selection, kbLinksPayloadPart omits knowledge_base_ids entirely — regardless of what the
    // (possibly stale) agentSettings cache resolved to — so PHLO-core preserves the stored set
    // instead of clobbering it with a transient-empty [] or a drifting session subset. It
    // also hands back flowData.phlo with knowledge_base_ids + the response-only marker stripped
    // (the marker must never be echoed back), ready for the payload spread.
    const {
      phloBaseForPayload,
      normalizedKnowledgeBaseIds,
      kbLinksPayloadPart,
      preserveExistingKbLinks,
    } = kbLinksForSave(flowData.phlo, options.selectedKBs, agentSettings?.knowledge_base_ids)
    // The subaccount tag is sent only when it changed in this session (see
    // sub-auth-id-for-save.ts); the loaded value is pulled out of the spread below so a
    // canvas-only save never echoes a tag this client may have loaded before another admin
    // changed it. config-service reads an absent key as "no change".
    const { sub_auth_id: loadedSubAuthId, ...phloBaseWithoutSubAuthId } = phloBaseForPayload
    const subAuthIdForSave = subAuthIdPayloadPart({
      stagedSubAuthId: agentSettings?.sub_auth_id,
      loadedSubAuthId,
    })
    const payloadEventCallbacks = filterEmptyEventCallbacks(agentSettings?.event_callbacks)
    const normalizedAgentSettingsForSave = agentSettings
      ? {
          ...agentSettings,
          schedule: normalizedScheduleForSave,
          // SER-5976 (F2): on an unknown-links save the payload OMITS knowledge_base_ids (see
          // kbLinksPayloadPart below) so PHLO-core preserves its stored set. The STORE SYNC must
          // branch off the SAME signal — otherwise setAgentSettings (in handleSuccess) would write
          // the stale/[] normalized ids into the flow store and the local UI would disagree with the
          // correctly-preserved server links until the flowData refetch lands. kbIdsForStoreSync
          // keeps the store's existing ids in that case (single source of truth as the payload).
          knowledge_base_ids: kbIdsForStoreSync(
            { normalizedKnowledgeBaseIds, preserveExistingKbLinks },
            useFlowStore.getState().agentSettings?.knowledge_base_ids,
          ),
          voice_ai_config: finalVoiceAiConfig,
          event_callbacks: agentSettings.event_callbacks,
          conversation_goals: agentSettings.conversation_goals,
        }
      : undefined

    // Get editor metadata for tracking who made changes
    const editorMetadata = useFlowStore.getState().getEditorMetadata()

    // Playground saves must not change the flow's lifecycle state — e.g. running
    // a PAUSED flow in the playground must keep it PAUSED, not flip it to ACTIVE.
    const isPlayground = options.isPlayground || false
    const publishState = isDraft ? AgentState.DRAFT : AgentState.ACTIVE
    const nextState = isPlayground ? flowData.phlo.state : publishState
    const nextEnabled = isPlayground ? flowData.phlo.enabled : !isDraft

    const payload = {
      ...data,
      is_playground: options.isPlayground || false,
      editor_metadata: editorMetadata,
      phlo: {
        ...phloBaseWithoutSubAuthId,
        name: options.name || flowData.phlo.name,
        enabled: nextEnabled,
        updated_at: Date.now(),
        group: 'public',
        phlo_type: flowType,
        description: 'Flow description',
        changed: true,
        redact: false,
        state: nextState,
        schedule: normalizedScheduleForSave,
        // SER-5976 (F2): `{ knowledge_base_ids }` normally; `{}` (omit-to-preserve) when the links
        // are unknown so PHLO-core preserves the existing set instead of clobbering it with a
        // transient-empty []. Decision unit-tested in kb-links-for-save.test.ts.
        ...kbLinksPayloadPart,
        voice_ai_config: normalizedAgentSettingsForSave?.voice_ai_config ?? finalVoiceAiConfig,
        event_callbacks: payloadEventCallbacks,
        conversation_goals: normalizedAgentSettingsForSave?.conversation_goals,
        // sub_auth_id rides the wholesale save like schedule/voice_ai_config, but only
        // when the user changed it here: `{}` (key absent = no change) or `{ sub_auth_id }`.
        ...subAuthIdForSave,
      },
      global_meta: data.global_meta
        ? {
            ...data.global_meta,
            ...(!isLivekitEnabledLoading && { livekit_ready: isLivekitEnabled }),
          }
        : data.global_meta,
    }

    const skipQueryInvalidation = !!options.skipQueryInvalidation

    await mutateFlowAsync(
      { payload, nodeMap, id: idToUse, skipQueryInvalidation },
      {
        onSuccess: (response: unknown) =>
          handleSuccess(
            response as FlowUpsertResponse,
            isDraft,
            options,
            nodeMap,
            normalizedAgentSettingsForSave,
          ),
        onError: (error) =>
          handleError(error, payload as unknown as RFlowPayload, nodeMap, options),
      },
    )
  }

  return {
    publishFlow,
    isPublishing: isPending,
    isSavingDraft: isPending && actionType === 'draft',
    isPublishingFlow: isPending && actionType === 'publish',
  }
}
